import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Save, Upload, Loader2, User as UserIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { apiFetch } from '@/utils/apiFetch';
import { fetchTenantProfile, getProfileIdentity, updateTenantProfile } from '@/lib/profileApi';
import { toast } from 'sonner';
import { useTranslation } from '@/components/utils/translations';

export default function SettingsProfile() {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [profilePictureUrl, setProfilePictureUrl] = useState('');

  const loadUserProfile = async () => {
    setIsLoading(true);
    try {
      const { identity, profile } = await fetchTenantProfile();

      setCurrentUser({
        id: identity.id,
        email: identity.email,
        full_name: identity.full_name
      });

      setDisplayName(profile.display_name || '');
      setJobTitle(profile.job_title || '');
      setCompanyName(profile.company_name || '');
      setProfilePictureUrl(profile.profile_photo_url || '');
    } catch (error) {
      console.error('Error loading user profile:', error);
      toast.error(t('failedToLoadProfile'));
      setDisplayName('');
      setJobTitle('');
      setCompanyName('');
      setProfilePictureUrl('');
      try {
        const { identity } = await getProfileIdentity();
        setCurrentUser({ id: identity.id, email: identity.email, full_name: identity.full_name });
      } catch {
        setCurrentUser(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserProfile();
  }, []);

  const handleSaveProfile = async () => {
    if (!currentUser?.id) return;
    setIsSaving(true);

    try {
      await updateTenantProfile({
        display_name: displayName,
        job_title: jobTitle,
        company_name: companyName
      });
      await loadUserProfile();
      toast.success(t('profileUpdatedSuccessfully'));
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('failedToSaveProfile'));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('pleaseSelectImageFile'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('imageSizeLessThan5MB'));
      return;
    }

    setIsUploadingPhoto(true);
    try {
      // Upload the file to our API
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiFetch('/api/upload/profile-photo', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const { file_url } = await response.json();

      await updateTenantProfile({ profile_photo_url: file_url });
      await loadUserProfile();
      toast.success(t('profilePhotoUpdatedSuccessfully'));
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(t('failedToUploadPhoto'));
    }
    setIsUploadingPhoto(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {t('updatePersonalInfoAndPhoto')}
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            {t('profilePhoto')}
          </CardTitle>
          <CardDescription>{t('updateYourProfilePicture')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {profilePictureUrl ? (
                  <img 
                    src={profilePictureUrl} 
                    alt={t('profile')}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserIcon className="w-12 h-12 text-gray-400" />
                )}
              </div>
              {isUploadingPhoto && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <div>
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
                disabled={isUploadingPhoto}
              />
              <label htmlFor="photo-upload">
                <Button 
                  variant="outline" 
                  disabled={isUploadingPhoto}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('photo-upload')?.click();
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {isUploadingPhoto ? t('uploading') : t('uploadPhoto')}
                </Button>
              </label>
              <p className="text-xs text-gray-500 mt-2">
                {t('photoFormatAndSizeHint')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('personalInformation')}</CardTitle>
          <CardDescription>
            {t('personalInfoGreetingUsage')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="displayName">{t('displayName')}</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('yourName')}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('displayNameUsageHint')}
            </p>
          </div>

          <div>
            <Label htmlFor="jobTitle">{t('jobTitle')}</Label>
            <Input
              id="jobTitle"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder={t('yourRoleOrPosition')}
            />
          </div>

          <div>
            <Label htmlFor="companyName">{t('companyName')}
            </Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder={t('yourOrganization')}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('usedInEmailSignatures')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Button 
        onClick={handleSaveProfile} 
        disabled={isSaving}
        className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('saving')}
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            {t('saveProfile')}
          </>
        )}
      </Button>
    </div>
  );
}